import { Location } from "@angular/common";
import { Inject, inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router
} from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AuthenticationApiService } from "../../core/authentication/authentication-api.service";
import { IpTokenService } from "../../core/authentication/iptoken.service";
import { OidcService } from "../../core/authentication/oidc.service";
import { EnvironmentService } from "../../core/environment.service";
import { LoginFlowNotSupportedError } from "../../core/errors/login-flow-not-supported-error";
import { LoadingService } from "../../core/loading-spinner/loading.service";
import { WINDOW } from "../../shared/app.window";
import { isEmptyString } from "../../shared/helpers";
import { routePaths } from "../../routes";
import { ILoginPageQueryparameters, PageMode, toSnakeCase } from "./query-params.model";


@Injectable()
export class LoginService {
  cacheKey: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly ipTokenService: IpTokenService,
    private readonly authenticationApiService: AuthenticationApiService,
    private readonly environmentService: EnvironmentService,
    private readonly oidcService: OidcService,
    private readonly location: Location,
    private readonly loadingService: LoadingService,
    @Inject(WINDOW) private readonly window: Window
  ) { }

  canActivate(snapshot: ActivatedRouteSnapshot): Observable<boolean> {
    const cachedNavigationResult = this.getCachedNavigationResult();
    if (cachedNavigationResult !== undefined) {
      return of(cachedNavigationResult);
    }

    this.prepareOidcContext(snapshot);

    return this.ipTokenService.getIpToken().pipe(
      map((token) => this.resolveNavigationForToken(token)),
      catchError(() => of(true))
    );
  }

  private getCachedNavigationResult(): boolean | undefined {
    if (!this.ipTokenService.ipIsChecked) {
      return undefined;
    }

    if (!this.ipTokenService.isOrganisationSelectionRequired()) {
      return true;
    }

    const previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString() ?? "";
    if (previousUrl.includes(`/${routePaths.loginAssistant}/`)) {
      return true;
    }

    this.router.navigate([`/${routePaths.loginAssistant}/${routePaths.organisations}`]);
    return false;
  }

  private prepareOidcContext(snapshot: ActivatedRouteSnapshot): void {
    this.oidcService.parseRoute(snapshot.queryParamMap);

    if (this.oidcService.isClientConfigAvailable) {
      return;
    }

    this.cacheKey = snapshot.queryParamMap.get("CacheKey");
    if (isEmptyString(this.cacheKey)) {
      this.redirectToStartPortal();
    }
  }

  private resolveNavigationForToken(token: string): boolean {
    if (!isEmptyString(token)) {
      this.router.navigate([`/${routePaths.loginAssistant}/${routePaths.groups}`]);
      return false;
    }

    if (this.ipTokenService.isOrganisationSelectionRequired()) {
      this.router.navigate([`/${routePaths.loginAssistant}/${routePaths.organisations}`]);
      return false;
    }

    return true;
  }

  private redirectToStartPortal() {
    this.window.location.href = this.environmentService.startPortalUrl;
  }

  public navigateToQrLogin(): void {
    this.navigateToLoginPage({
      PageMode: PageMode.QrCode
    });
  }

  private navigateToLogin(loginName: string, pageMode: PageMode): void {
    this.navigateToLoginPage({
      LoginHint: loginName,
      PageMode: pageMode
    });
  }

  private navigateToLoginPage(pageParams: ILoginPageQueryparameters): void {
    this.router.navigate(["/"], {
      queryParams: toSnakeCase(pageParams), queryParamsHandling: "merge"
    });
  }

  private authenticateUser() {
    const forwardUrl = this.location.prepareExternalUrl(`/authorize?${this.oidcService.openIdClientString}`);
    this.window.location.href = forwardUrl;
  }

  checkLoginUrl(username: string): Observable<string> {
    return this.authenticationApiService.getLoginUrl(username).pipe(map(forwardUrl => {
      if (forwardUrl.indexOf("Authentication/#!/picture") >= 0) {
        this.navigateToLogin(username, PageMode.Picture);
      } else if (forwardUrl.toLowerCase().indexOf("qrcode") >= 0) {
        this.navigateToQrLogin();
      }
      else if (this.windowsWebSignInAndAzureAsIdp(forwardUrl)) {
        throw new LoginFlowNotSupportedError("WindowsWebSignInAndAzureAsIdp");
      }
      else {
        if (this.oidcService.isClientConfigAvailable) {
          this.window.location.href = forwardUrl.toLocaleLowerCase();
        }
        else {
          this.window.location.href = this.appendCacheKey(forwardUrl);
        }
      }
      return forwardUrl;
    }));
  }

  appendCacheKey(url: string): string {
    //its expected that URl already has other parameters so no need to check/add "?""
    return url += "&CacheKey=" + this.cacheKey;
  }

  login(token: string): void {
    this.loadingService.show();
    if (this.oidcService.isClientConfigAvailable) {
      this.authenticateUser();
    } else {
      this.authenticationApiService.loginWithToken(token, this.cacheKey!);
    }
  }

  private windowsWebSignInAndAzureAsIdp(forwardUrl: string): boolean {
    const windowsWebSignIn = this.window.navigator.userAgent.toLocaleLowerCase().includes("edge/1");
    const azureAsIdp = forwardUrl.toLocaleLowerCase().includes("loginforaad=1");
    return windowsWebSignIn && azureAsIdp;
  }
}

export const canActivateLogin: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  return inject(LoginService).canActivate(route);
};
